import React, { useState, useEffect } from 'react'
import { Check, ListFilter, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { api } from '../../services/api'

export interface UserFilters {
  status: string[]
  role: string[]
  collegeId: string[]
}

interface UserFilterDrawerProps {
  isOpen: boolean
  onClose: () => void
  onApplyFilters: (filters: UserFilters) => void
  currentFilters: UserFilters
  currentUserRole?: string
}

interface College {
  id: string
  name: string
  code: string
}

const UserFilterDrawer: React.FC<UserFilterDrawerProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  currentFilters,
  currentUserRole
}) => {
  const [tempFilters, setTempFilters] = useState<UserFilters>(currentFilters)
  const [colleges, setColleges] = useState<College[]>([])
  const [isLoadingColleges, setIsLoadingColleges] = useState(false)
  
  // Check if current user is college admin
  const isCollegeAdmin = currentUserRole === 'COLLEGE_ADMIN'

  // Update temp filters when current filters change
  useEffect(() => {
    setTempFilters(currentFilters)
  }, [currentFilters])

  // Fetch colleges when component mounts
  useEffect(() => {
    if (isOpen) {
      fetchColleges()
    }
  }, [isOpen])

  const fetchColleges = async () => {
    setIsLoadingColleges(true)
    try {
      const response = await api.get('/colleges', { 
        params: { limit: 100 } // Get all colleges
      })
      if (response.data?.data) {
        setColleges(response.data.data)
      }
    } catch (error) {
      console.error('Failed to fetch colleges:', error)
    } finally {
      setIsLoadingColleges(false)
    }
  }

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ]

  const allRoleOptions = [
    { value: 'SYSTEM_ADMIN', label: 'System Admin' },
    { value: 'COLLEGE_ADMIN', label: 'College Admin' },
    { value: 'ACCOUNTANT', label: 'Accountant' },
    { value: 'STAFF', label: 'Staff' },
    { value: 'FACULTY', label: 'Faculty' }
  ]

  // Filter role options for college admins
  const roleOptions = isCollegeAdmin 
    ? allRoleOptions.filter(role => role.value !== 'SYSTEM_ADMIN')
    : allRoleOptions

  const handleStatusChange = (statusValue: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      status: checked 
        ? [...prev.status, statusValue]
        : prev.status.filter(s => s !== statusValue)
    }))
  }

  const handleRoleChange = (roleValue: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      role: checked 
        ? [...prev.role, roleValue]
        : prev.role.filter(r => r !== roleValue)
    }))
  }

  const handleCollegeChange = (collegeId: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      collegeId: checked 
        ? [...prev.collegeId, collegeId]
        : prev.collegeId.filter(c => c !== collegeId)
    }))
  }

  const handleApply = () => {
    onApplyFilters(tempFilters)
    onClose()
  }

  const handleClearAll = () => {
    const clearedFilters: UserFilters = {
      status: [],
      role: [],
      collegeId: isCollegeAdmin ? tempFilters.collegeId : [] // Preserve collegeId for college admins
    }
    setTempFilters(clearedFilters)
    onApplyFilters(clearedFilters)
    onClose()
  }

  const getActiveFilterCount = () => {
    return tempFilters.status.length + tempFilters.role.length + (isCollegeAdmin ? 0 : tempFilters.collegeId.length)
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ListFilter className="h-5 w-5 text-gray-600" />
            Filters
            {getActiveFilterCount() > 0 && (
              <Badge className="h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                {getActiveFilterCount()}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="grid flex-1 auto-rows-min gap-6 px-4">
          {/* Status Filter */}
          <div className="grid gap-3">
            <Label className="text-sm font-medium text-gray-900">Status</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">
                    {tempFilters.status.length === 0
                      ? "Select status..."
                      : tempFilters.status.length === 1
                      ? statusOptions.find((option) => option.value === tempFilters.status[0])
                          ?.label
                      : `${tempFilters.status.length} statuses selected`}
                  </span>
                  <div className="flex items-center gap-x-2 text-gray-900">
                    {tempFilters.status.length > 0 && (
                      <Badge className="ml-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                        {tempFilters.status.length}
                      </Badge>
                    )}
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[22rem] p-3" align="start">
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    Select status
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {statusOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-3"
                      >
                        <Checkbox
                          id={`status-${option.value}`}
                          checked={tempFilters.status.includes(option.value)}
                          onCheckedChange={(checked) =>
                            handleStatusChange(option.value, !!checked)
                          }
                        />
                        <Label
                          htmlFor={`status-${option.value}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Role Filter */}
          <div className="grid gap-3">
            <Label className="text-sm font-medium text-gray-900">Role</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal"
                  disabled={isLoadingColleges}
                >
                  <span className="truncate">
                    {tempFilters.role.length === 0
                      ? "Select roles..."
                      : tempFilters.role.length === 1
                      ? roleOptions.find((r) => r.value === tempFilters.role[0])
                          ?.label
                      : `${tempFilters.role.length} roles selected`}
                  </span>
                  <div className="flex items-center gap-x-2 text-gray-900">
                    {tempFilters.role.length > 0 && (
                      <Badge className="ml-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                        {tempFilters.role.length}
                      </Badge>
                    )}
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[22rem] p-3" align="start">
                <div className="space-y-3">
                  <div className="text-xs font-medium text-muted-foreground">
                    Select user roles
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {roleOptions.map((role) => (
                      <div
                        key={role.value}
                        className="flex items-center space-x-3"
                      >
                        <Checkbox
                          id={`role-${role.value}`}
                          checked={tempFilters.role.includes(role.value)}
                          onCheckedChange={(checked) =>
                            handleRoleChange(role.value, !!checked)
                          }
                        />
                        <Label
                          htmlFor={`role-${role.value}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {role.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* College Filter - Hidden for college admins */}
          {!isCollegeAdmin && (
            <div className="grid gap-3">
              <Label className="text-sm font-medium text-gray-900">College</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between font-normal"
                    disabled={isLoadingColleges}
                  >
                    <span className="truncate">
                      {isLoadingColleges
                        ? "Loading colleges..."
                        : tempFilters.collegeId.length === 0
                        ? "Select colleges..."
                        : tempFilters.collegeId.length === 1
                        ? colleges.find((c) => c.id === tempFilters.collegeId[0])
                            ?.name
                        : `${tempFilters.collegeId.length} colleges selected`}
                    </span>
                    <div className="flex items-center gap-x-2 text-gray-900">
                      {tempFilters.collegeId.length > 0 && (
                        <Badge className="ml-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                          {tempFilters.collegeId.length}
                        </Badge>
                      )}
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[22rem] p-3" align="start">
                  <div className="space-y-3">
                    <div className="text-xs text-muted-foreground">
                      Select colleges
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {isLoadingColleges ? (
                        <div className="text-sm text-gray-500 text-center py-4">
                          Loading colleges...
                        </div>
                      ) : colleges.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-4">
                          No colleges available
                        </div>
                      ) : (
                        colleges.map((college) => (
                          <div
                            key={college.id}
                            className="flex items-center space-x-3"
                          >
                            <Checkbox
                              id={`college-${college.id}`}
                              checked={tempFilters.collegeId.includes(college.id)}
                              onCheckedChange={(checked) =>
                                handleCollegeChange(college.id, !!checked)
                              }
                            />
                            <Label
                              htmlFor={`college-${college.id}`}
                              className="text-sm font-normal cursor-pointer flex-1"
                            >
                              <div>{college.name}</div>
                              <div className="text-xs text-gray-500">
                                {college.code}
                              </div>
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        <SheetFooter>
          <Button onClick={handleApply} className="w-full">
            <Check className="h-4 w-4" />
            Apply Filters
          </Button>
          <Button
            variant="outline"
            onClick={handleClearAll}
            className="w-full"
            disabled={getActiveFilterCount() === 0}
          >
            Clear All Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default UserFilterDrawer
